/**
 * AppPermissionsSummaryCards: the App inventory's risk-tier filter as Carbon
 * contained tabs with count tags (mockup 03, the severity tabs above the
 * table): All, then Critical, High, Medium and Low with the tenant's count in
 * each. Choosing a tab filters the table by tier, as clicking a card did. The
 * counts hide while the summary loads, so a 0 never stands in for "unknown".
 *
 * The export name and props are unchanged (AppPostureTab passes the tabs to
 * the table's `tabs` slot). The stale count moved to the table description.
 */

import { ContainedTabs, type ContainedTab } from '@/components/carbon/ContainedTabs'
import type { OAuthAppSummary } from './appPermissionsTypes'

interface TabConfig {
  id: string
  label: string
  getValue: (s: OAuthAppSummary) => number
  /** The risk tier the table filters by; undefined for All. */
  filterValue?: string
}

const TABS: TabConfig[] = [
  { id: 'all', label: 'All', getValue: s => s.totalApps },
  { id: 'Critical', label: 'Critical', getValue: s => s.criticalCount, filterValue: 'Critical' },
  { id: 'High', label: 'High', getValue: s => s.highCount, filterValue: 'High' },
  { id: 'Medium', label: 'Medium', getValue: s => s.mediumCount, filterValue: 'Medium' },
  { id: 'Low', label: 'Low', getValue: s => s.lowCount, filterValue: 'Low' },
]

interface AppPermissionsSummaryCardsProps {
  summary: OAuthAppSummary | undefined
  isLoading: boolean
  onFilterClick: (riskTier: string | undefined) => void
  activeFilter: string | undefined
}

export function AppPermissionsSummaryCards({ summary, isLoading, onFilterClick, activeFilter }: AppPermissionsSummaryCardsProps) {
  const tabs: ContainedTab[] = TABS.map(tab => {
    const value = summary && !isLoading ? tab.getValue(summary) : undefined
    return {
      id: tab.id,
      label: tab.label,
      count: typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString('en-US') : null,
    }
  })
  const selected = TABS.find(t => t.filterValue !== undefined && t.filterValue === activeFilter)?.id ?? 'all'

  return (
    <ContainedTabs
      aria-label="Risk tier"
      tabs={tabs}
      value={selected}
      onChange={id => onFilterClick(TABS.find(t => t.id === id)?.filterValue)}
    />
  )
}
